import { openNotificationDrawer } from "@components/utils/NotificationDrawer/utils";
import { envConfig } from "@configs/env";
import { useStatsigTracking } from "@configs/statsig";
import { NotificationDrawerEnabledStatus } from "@constants/misc";
import { NOTIFICATION_DRAWER_ROUTES } from "@constants/paths";
import useAuthSession from "@hooks/useAuthSession";
import { NotificationPermissionState } from "@types";
import { getDevFlags } from "@utils/dev-flags";
import { getNotificationPermission, isNotificationSupported } from "@utils/notification";
import { SW_MEMBER_ID_STORAGE_KEY, delay, getPersistedDueAt, persistDueAt, updatePushSubscription } from "kz-ui-sdk";
import moment from "moment";
import { useCallback, useMemo } from "react";
import { matchPath } from "react-router-dom";

const SEVEN_DAYS_IN_SECONDS = 7 * 24 * 60 * 60;
const NOTIFICATION_DRAWER_REMINDER_KEY = "notification-drawer-reminder";

const NOTIFICATION_DRAWER_DELAY = 100;

export const useNotificationHandler = () => {
  const { self } = useAuthSession();

  const reminderKey = useMemo(() => {
    const memberId = self?.member?.id;
    return memberId ? `${NOTIFICATION_DRAWER_REMINDER_KEY}:${memberId}` : `${NOTIFICATION_DRAWER_REMINDER_KEY}:guest`;
  }, [self?.member?.id]);

  const { trackNotificationSubscriptionStatus, trackNotificationBrowserPromptShown } = useStatsigTracking();

  const permission = getNotificationPermission();

  const notificationDrawerEnabledStatus = useMemo(() => {
    const devFlagValue = getDevFlags()?.notificationDrawerEnabled;
    return devFlagValue === false ? NotificationDrawerEnabledStatus.DISABLED : NotificationDrawerEnabledStatus.ENABLED;
  }, []);

  const shouldPrompt = useMemo(() => {
    return (
      notificationDrawerEnabledStatus === NotificationDrawerEnabledStatus.ENABLED &&
      isNotificationSupported() &&
      permission === NotificationPermissionState.DEFAULT &&
      !!import.meta.env.VITE_PUSH_NOTIFICATION_URI &&
      !!self?.member?.id
    );
  }, [permission, notificationDrawerEnabledStatus, self?.member?.id]);

  const snooze = useCallback(() => {
    persistDueAt(reminderKey, moment().add(SEVEN_DAYS_IN_SECONDS, "seconds"));
  }, [reminderKey]);

  // Ask for permission and subscribe to push notifications
  const requestPermissionAndSubscribe = useCallback(
    async (alwaysSubscribe: boolean = false) => {
      if (!isNotificationSupported()) return NotificationPermissionState.DENIED;
      let result: NotificationPermissionState = permission;
      if (permission === NotificationPermissionState.DEFAULT) {
        try {
          trackNotificationBrowserPromptShown();
          result = (await Notification.requestPermission()) as NotificationPermissionState;
        } catch (_) {
          result = NotificationPermissionState.DENIED;
        }
      }

      if (result === NotificationPermissionState.GRANTED && self?.member?.id) {
        try {
          const registeredMemberId = sessionStorage.getItem(SW_MEMBER_ID_STORAGE_KEY);
          // if member id is not registered or different, update notification subscription
          if (!registeredMemberId || registeredMemberId !== self.member.id || alwaysSubscribe)
            await updatePushSubscription({
              memberId: self.member.id,
              accountId: envConfig.accountId,
              notificationURI: import.meta.env.VITE_PUSH_NOTIFICATION_URI,
              notificationTestParam: import.meta.env.VITE_TEST_NOTIFICATION_PARAM,
              publicUrl: import.meta.env.VITE_PUBLIC_URL,
            });
        } catch (_) {
          // ignore
        }
      }
      trackNotificationSubscriptionStatus(result);
      return result;
    },
    [permission, self?.member?.id, trackNotificationSubscriptionStatus, trackNotificationBrowserPromptShown],
  );

  // Check(but not ask) if permission is granted and subscribe to push notifications
  const checkPermissionAndSubscribe = useCallback(async () => {
    const permission = getNotificationPermission();
    if (permission === NotificationPermissionState.GRANTED && self?.member?.id) {
      await requestPermissionAndSubscribe();
    }
  }, [requestPermissionAndSubscribe, self?.member?.id]);

  const openIfEligible = useCallback(async () => {
    if (!shouldPrompt) return;
    // Delay to avoid race condition with the route change
    await delay(NOTIFICATION_DRAWER_DELAY);
    const isNotificationDrawerAllowedRoute = NOTIFICATION_DRAWER_ROUTES.some((route) =>
      matchPath(route, window.location.pathname),
    );
    if (!isNotificationDrawerAllowedRoute) return;
    const nextDisplayAt = getPersistedDueAt(reminderKey);

    const isDue = nextDisplayAt ? moment().isAfter(nextDisplayAt) : true;
    if (!isDue) return;
    openNotificationDrawer();
  }, [shouldPrompt, reminderKey]);

  return {
    shouldPrompt,
    snooze,
    requestPermissionAndSubscribe,
    checkPermissionAndSubscribe,
    openIfEligible,
    notificationDrawerEnabledStatus,
  };
};
